/**
 * Ship Sinking — Solo page entry point.
 *
 * Orchestrates difficulty selection → fleet placement → combat → game over.
 * Auto-discovered by esbuild from app/static/ts/pages/.
 */

import { logger } from '../lib/logger';
import { showError, showSuccess, showInfo } from '../lib/toast';
import { showResumeBanner } from '../lib/resume-banner';
import { showEndOfGameOverlay, buildSoloWinConfig, buildSoloBustConfig } from '../components/end-of-game-overlay';
import type { NextGameSuggestions } from '../components/end-of-game-overlay';
import { fireGuestEvent, fireGameCompleted, hookGuestCompletionToEOG } from '../lib/guest-conversion-engine';
import { isGuestUser } from '../lib/auth-state';
import { initAnimationsModal } from '../components/animation-tier-modal';
import { getAnimationPref } from '../lib/animation-pref';
import type { AnimationTier } from '../lib/animation-pref';
import { safeInitCelebrations } from '../utils/celebration-handler';
import { runSequence, type SequenceHandle } from '../lib/animation-orchestrator';
import { celebrationEffect, delayEffect, EOG_BUFFER_MS } from '../lib/animation-effects';
import { authModal, ensureAuthModalExists } from '../components/auth-modal';
import { balanceService } from '../services/balance-service';
import { UserAPI } from '../utils/user-api';
import { mountOpponentBar, getSelectedOpponent } from '../components/opponent-bar';
import type { AIOpponent } from '../components/opponent-picker';
import { showWeaponUnlockCard } from '../components/weapon-unlock-card';
import * as api from '../games/ship_sinking/api';
import * as grid from '../games/ship_sinking/grid';
import type {
  Ship,
  ShotResponse,
  AbilityResultResponse,
  GameStateResponse,
  FleetSpec,
  AbilityName,
  AIActionResult,
  AbilitiesMap,
  OpponentInfo,
} from '../games/ship_sinking/types';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface GameState {
  gameId: string;
  difficulty: string;
  gridSize: number;
  fleet: FleetSpec[];
  phase: 'selection' | 'placement' | 'combat' | 'finished';
  turnCount: number;
  yourTurn: boolean;
  activeAbility: AbilityName | null;
  ownShips: Ship[];
  processing: boolean;
  abilities?: AbilitiesMap;
  opponentInfo?: OpponentInfo | null;
  isWin?: boolean;
}

let state: GameState | null = null;
let placementState: grid.PlacementState | null = null;
let attackTable: HTMLTableElement | null = null;
let defenseTable: HTMLTableElement | null = null;
let currentOpponent: AIOpponent | null = null;
let pendingEntryContext: string | undefined;
let isGuest = false;
let animationLevel: AnimationTier = 'playful';
const pendingTimers = new Set<number>();
let eogHandle: SequenceHandle | undefined;

// Tracks previous ability availability to detect locked → available transitions
const _prevAbilityAvailable: Record<string, boolean> = {};

// ---------------------------------------------------------------------------
// Guest status
// ---------------------------------------------------------------------------

async function loadGuestStatus(): Promise<void> {
  try {
    const resp = await fetch('/me', { credentials: 'same-origin' });
    if (resp.ok) {
      const data = await resp.json();
      isGuest = isGuestUser(data);
    } else {
      isGuest = true;
    }
  } catch {
    isGuest = true;
  }
  logger.info('[ShipSinking] Guest status:', isGuest);
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function show(id: string): void {
  const el = document.getElementById(id);
  if (el) el.style.display = '';
}

function hide(id: string): void {
  const el = document.getElementById(id);
  if (el) el.style.display = 'none';
}

/** Apply a grid-size CSS class (e.g. grid-6x6) to the container so CSS can
 *  scale cells to fill their smaller grid appropriately. */
function applyGridSizeClass(containerId: string, gridSize: number): void {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.classList.remove('grid-6x6', 'grid-7x7', 'grid-8x8');
  if (gridSize <= 6) el.classList.add('grid-6x6');
  else if (gridSize <= 7) el.classList.add('grid-7x7');
  else if (gridSize <= 8) el.classList.add('grid-8x8');
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

async function init(): Promise<void> {
  logger.info('[ShipSinking] Page init');

  // Load guest status, then stats (must await guest status before checking isGuest)
  loadGuestStatus().then(() => loadStats());

  // Animation modal (HTML added in Phase 4; safe no-op until then)
  animationLevel = getAnimationPref();
  initAnimationsModal({
    gamePrefix: 'ship',
    cssClassPrefix: 'ss-anim',
    pageElementId: 'shipSinkingPage',
    onTierChange: (tier) => { animationLevel = tier as AnimationTier; },
  });

  safeInitCelebrations();

  // Read opponent from URL param (?opponent=<id>)
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedOpponentId = urlParams.get('opponent');

  // Mount opponent bar (shows selected opponent + Change option)
  const barContainer = document.getElementById('opponentBarContainer');
  if (barContainer) {
    mountOpponentBar({
      container: barContainer,
      gameType: 'ship_sinking',
      initialOpponentId: preselectedOpponentId,
      onOpponentChange: (opp) => {
        currentOpponent = opp;
      },
    }).then((opponent) => {
      currentOpponent = opponent;
    });
  }

  // Difficulty selection (event delegation)
  document.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.gc-card[data-difficulty]') as HTMLElement | null;
    if (btn) {
      const difficulty = btn.dataset.difficulty;
      if (difficulty) {
        if (currentOpponent) {
          startGameWithOpponent(currentOpponent, difficulty);
        } else {
          startGame(difficulty);
        }
      }
    }
  });

  // Placement controls
  document.getElementById('rotateBtn')?.addEventListener('click', handleRotate);
  document.getElementById('autoPlaceBtn')?.addEventListener('click', handleAutoPlace);
  document.getElementById('readyBtn')?.addEventListener('click', handleReady);

  // Ability buttons (event delegation)
  document.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.ss-ability-btn') as HTMLButtonElement | null;
    if (btn && !btn.disabled) {
      const ability = btn.dataset.ability as AbilityName | undefined;
      if (ability) toggleAbilityMode(ability);
    }
  });

  // Depth charge modal fire
  document.getElementById('dcFireBtn')?.addEventListener('click', handleDepthChargeFire);

  // New game button
  document.getElementById('newGameBtn')?.addEventListener('click', resetToSelection);

  // Tooltips (info button + ability buttons)
  if (typeof bootstrap !== 'undefined') {
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
      new bootstrap.Tooltip(el);
    });
    const infoBtn = document.getElementById('infoBtn');
    if (infoBtn) new bootstrap.Tooltip(infoBtn);
  }

  // Check for active game to resume (must complete before auto-start)
  const hasActiveGame = await checkActiveGame();

  // Read URL params for guided play flow
  const ecParam = urlParams.get('ec');
  const difficultyParam = urlParams.get('difficulty');
  if (ecParam) {
    pendingEntryContext = ecParam;
  }
  if (urlParams.has('ec') || urlParams.has('difficulty')) {
    history.replaceState({}, '', window.location.pathname);
  }

  // Auto-start only if no active game is being resumed
  if (!hasActiveGame && difficultyParam && difficultyParam !== 'auto') {
    if (currentOpponent) {
      void startGameWithOpponent(currentOpponent, difficultyParam);
    } else {
      void startGame(difficultyParam);
    }
  } else if (!hasActiveGame && difficultyParam === 'auto') {
    // 'auto' → try saved preference, fall back to selection screen
    const saved = await UserAPI.getPreference('ship_sinking_difficulty').catch(() => null);
    if (typeof saved === 'string' && saved) {
      if (currentOpponent) {
        void startGameWithOpponent(currentOpponent, saved);
      } else {
        void startGame(saved);
      }
    }
  }
  // No param or no saved pref → show selection screen
}

// ---------------------------------------------------------------------------
// Resume Active Game
// ---------------------------------------------------------------------------

async function checkActiveGame(): Promise<boolean> {
  try {
    const resp = await fetch('/api/games/ship-sinking/active', { credentials: 'same-origin' });
    if (!resp.ok) return false;
    const data = await resp.json();
    if (!data.game) return false;

    const res = data.game;
    state = {
      gameId: res.game_id,
      difficulty: res.difficulty,
      gridSize: res.grid_size,
      fleet: res.fleet,
      phase: res.phase === 'combat' ? 'combat' : 'placement',
      turnCount: res.turn_count || 0,
      yourTurn: res.your_turn ?? true,
      activeAbility: null,
      ownShips: res.own_grid ?? [],
      processing: false,
      abilities: res.abilities,
      opponentInfo: res.opponent ?? null,
    };

    if (res.phase === 'combat') {
      enterCombatPhase(res, res.abilities);
    } else {
      placementState = grid.createPlacementState(res.grid_size, res.fleet);
      enterPlacementPhase();
    }

    // Show resume banner in the active section
    const activeSection = document.getElementById(
      res.phase === 'combat' ? 'combatSection' : 'placementSection'
    );
    if (activeSection) {
      showResumeBanner({
        container: activeSection,
        onAbandon: async () => {
          try {
            await fetch('/api/games/ship-sinking/abandon', { method: 'POST' });
          } catch (err) {
            logger.error('Failed to abandon game:', err);
          }
          state = null;
          placementState = null;
          resetToSelection();
        },
      });
    }

    logger.info('[ShipSinking] Resumed active game:', res.game_id);
    return true;
  } catch (err) {
    logger.warn('[ShipSinking] Failed to check active game', err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Game flow
// ---------------------------------------------------------------------------

async function startGame(difficulty: string): Promise<void> {
  logger.info('[ShipSinking] Starting game:', difficulty);

  // Consume pending entry context (one-shot)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  try {
    const res = await api.startGame(difficulty, ec);

    state = {
      gameId: res.game_id,
      difficulty: res.difficulty,
      gridSize: res.grid_size,
      fleet: res.fleet,
      phase: res.phase === 'combat' ? 'combat' : 'placement',
      turnCount: 0,
      yourTurn: res.your_turn ?? true,
      activeAbility: null,
      ownShips: res.player_grid ?? [],
      processing: false,
      abilities: res.abilities,
    };

    if (res.phase === 'combat') {
      // Easy mode: fleet auto-placed, skip to combat
      enterCombatPhase(undefined, res.abilities);
    } else {
      placementState = grid.createPlacementState(res.grid_size, res.fleet);
      enterPlacementPhase();
    }

    // Save difficulty preference (authenticated users only)
    if (!isGuest) {
      UserAPI.setPreference('ship_sinking_difficulty', difficulty).catch(() => {});
    }
  } catch (err) {
    showError('Could not start game. Try again.');
    logger.error('[ShipSinking] startGame error:', err);
  }
}

async function startGameWithOpponent(opponent: AIOpponent, difficulty?: string): Promise<void> {
  logger.info('[ShipSinking] Starting game vs', opponent.display_name, difficulty ? `(${difficulty})` : '');

  // Consume pending entry context (one-shot)
  const ec = pendingEntryContext;
  pendingEntryContext = undefined;

  try {
    const res = await api.startGameWithOpponent(opponent.id, difficulty, ec);

    state = {
      gameId: res.game_id,
      difficulty: res.difficulty,
      gridSize: res.grid_size,
      fleet: res.fleet,
      phase: res.phase === 'combat' ? 'combat' : 'placement',
      turnCount: 0,
      yourTurn: res.your_turn ?? true,
      activeAbility: null,
      ownShips: res.player_grid ?? [],
      processing: false,
      abilities: res.abilities,
      opponentInfo: res.opponent ?? null,
    };

    if (res.phase === 'combat') {
      enterCombatPhase(undefined, res.abilities);
    } else {
      placementState = grid.createPlacementState(res.grid_size, res.fleet);
      enterPlacementPhase();
    }

    // Save difficulty preference (authenticated users only)
    if (difficulty && !isGuest) {
      UserAPI.setPreference('ship_sinking_difficulty', difficulty).catch(() => {});
    }
  } catch (err) {
    showError('Could not start game. Try again.');
    logger.error('[ShipSinking] startGameWithOpponent error:', err);
  }
}

function enterPlacementPhase(): void {
  if (!state || !placementState) return;
  window.__versionGuard?.setSafeToReload(false);

  hide('selectionSection');
  show('placementSection');
  hide('combatSection');

  // Render the placement grid
  const table = grid.renderGrid({
    gridSize: state.gridSize,
    containerId: 'placementGrid',
    label: 'Your Fleet',
    interactive: true,
    onCellClick: handlePlacementClick,
    testIdPrefix: 'placement',
  });
  // Apply grid-size class so CSS can scale cells for smaller grids
  applyGridSizeClass('placementGrid', state.gridSize);

  // Hover preview during placement
  table.addEventListener('mouseover', (e) => {
    if (!placementState || !defenseTable) return;
    if (placementState.currentShipIndex >= placementState.fleet.length) return;
    const td = (e.target as HTMLElement).closest('td.ss-cell') as HTMLTableCellElement | null;
    if (!td) return;
    const row = parseInt(td.dataset.row ?? '', 10);
    const col = parseInt(td.dataset.col ?? '', 10);
    if (isNaN(row) || isNaN(col)) return;
    const cells = grid.previewPlacement(placementState, row, col);
    grid.showPlacementPreview(defenseTable, cells);
    if (!cells) {
      showInvalidHoverPreview(row, col);
    }
  });
  table.addEventListener('mouseleave', () => {
    if (defenseTable) grid.clearPreview(defenseTable);
  });

  // Touch placement — two-tap: first tap shows preview, second tap confirms
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (isTouchDevice) {
    let pendingPreviewCells: string | null = null; // stringified cells for comparison

    // Helper: find grid cell coordinates under a touch point
    function cellFromTouch(touch: Touch): { row: number; col: number } | null {
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const td = el?.closest('td.ss-cell') as HTMLTableCellElement | null;
      if (!td) return null;
      const row = parseInt(td.dataset.row ?? '', 10);
      const col = parseInt(td.dataset.col ?? '', 10);
      if (isNaN(row) || isNaN(col)) return null;
      return { row, col };
    }

    // Show touch-style (pulsing) preview at given cells
    function showTouchPreview(cells: [number, number][]): void {
      if (!defenseTable) return;
      grid.showPlacementPreview(defenseTable, cells);
      // Mark cells as "touch pending" so CSS can apply pulsing animation
      for (const [r, c] of cells) {
        const cell = defenseTable.querySelector(`td[data-row="${r}"][data-col="${c}"]`);
        if (cell) cell.classList.add('ss-cell-preview-touch');
      }
    }

    function clearTouchPreview(): void {
      if (defenseTable) {
        defenseTable.querySelectorAll('.ss-cell-preview-touch').forEach(el => {
          el.classList.remove('ss-cell-preview-touch');
        });
        grid.clearPreview(defenseTable);
      }
      pendingPreviewCells = null;
      hintEl.textContent = 'Tap the grid to preview placement';
    }

    // Floating hint below the grid
    const hintEl = document.createElement('p');
    hintEl.className = 'ss-touch-hint text-center text-muted small mt-2';
    hintEl.dataset.testid = 'touch-placement-hint';
    hintEl.textContent = 'Tap the grid to preview placement';
    const gridContainer = document.getElementById('placementGrid');
    gridContainer?.insertAdjacentElement('afterend', hintEl);

    table.addEventListener('touchmove', (e) => {
      e.preventDefault(); // prevent page scroll during placement drag
      if (!placementState) return;
      if (placementState.currentShipIndex >= placementState.fleet.length) return;
      const touch = e.touches[0];
      const pos = cellFromTouch(touch);
      if (!pos) return;
      const cells = grid.previewPlacement(placementState, pos.row, pos.col);
      showTouchPreview(cells ?? []);
      pendingPreviewCells = cells ? JSON.stringify(cells) : null;
      hintEl.textContent = cells ? 'Lift finger to confirm, or keep dragging' : 'Can\'t place here';
    }, { passive: false });

    table.addEventListener('touchend', (e) => {
      if (!placementState || !defenseTable) return;
      if (placementState.currentShipIndex >= placementState.fleet.length) return;
      const touch = e.changedTouches[0];
      const pos = cellFromTouch(touch);
      if (!pos) { clearTouchPreview(); return; }

      const cells = grid.previewPlacement(placementState, pos.row, pos.col);
      const cellsKey = cells ? JSON.stringify(cells) : null;

      if (pendingPreviewCells !== null && pendingPreviewCells === cellsKey && cells) {
        // Second tap on same cells → confirm placement
        grid.confirmPlacement(placementState, cells);
        rebuildOwnShips();
        clearTouchPreview();
        grid.drawShips(defenseTable, state!.ownShips);
        updateShipList();
        updateReadyButton();
      } else if (cells) {
        // First tap → show preview
        showTouchPreview(cells);
        pendingPreviewCells = cellsKey;
        hintEl.textContent = 'Tap again to confirm placement';
      } else {
        // Invalid position
        clearTouchPreview();
        hintEl.textContent = 'Can\'t place here — try again';
      }
    });

    table.addEventListener('touchcancel', () => {
      clearTouchPreview();
    });
  }

  // Re-use defense table for placement reference
  defenseTable = table;

  updateShipList();
  updateReadyButton();
}

function enterCombatPhase(gameState?: GameStateResponse, initialAbilities?: AbilitiesMap): void {
  if (!state) return;
  window.__versionGuard?.setSafeToReload(false);
  state.phase = 'combat';

  hide('selectionSection');
  hide('placementSection');
  show('combatSection');

  // Render attack grid (enemy waters)
  attackTable = grid.renderGrid({
    gridSize: state.gridSize,
    containerId: 'attackGrid',
    label: 'Enemy Waters',
    interactive: true,
    onCellClick: handleAttackClick,
    testIdPrefix: 'attack',
  });
  applyGridSizeClass('attackGrid', state.gridSize);

  // Render defense grid (own fleet — non-interactive, show incoming)
  defenseTable = grid.renderGrid({
    gridSize: state.gridSize,
    containerId: 'defenseGrid',
    label: 'Your Fleet',
    interactive: false,
    testIdPrefix: 'defense',
  });
  applyGridSizeClass('defenseGrid', state.gridSize);

  // Draw own ships on defense grid
  if (defenseTable && state.ownShips.length > 0) {
    grid.drawShips(defenseTable, state.ownShips);
  }

  // If restoring state, draw existing shots
  if (gameState) {
    if (attackTable && gameState.own_shots) {
      grid.drawShots(attackTable, gameState.own_shots);
    }
    if (defenseTable && gameState.incoming_shots) {
      grid.drawShots(defenseTable, gameState.incoming_shots);
    }
    // Draw sunk ships on own grid
    if (defenseTable && gameState.own_grid) {
      grid.drawShips(defenseTable, gameState.own_grid);
    }
    state.turnCount = gameState.turn_count;
    state.yourTurn = gameState.your_turn;
    updateAbilities(gameState.abilities);
    updateFleetTracker(gameState.fleet_tracker);
  } else {
    // Fresh combat entry: build initial abilities and fleet tracker
    if (initialAbilities) {
      updateAbilities(initialAbilities);
    }
    // Build initial fleet tracker from fleet specs (all unsunk)
    const initialTracker = state.fleet.map(f => ({
      type: f.type,
      size: f.size,
      label: f.label,
      sunk: false,
    }));
    updateFleetTracker(initialTracker);
  }

  updateTurnIndicator();
  updateAbilityButtons();
}

// ---------------------------------------------------------------------------
// Placement handlers
// ---------------------------------------------------------------------------

function handlePlacementClick(row: number, col: number): void {
  if (!state || !placementState || !defenseTable) return;
  if (placementState.currentShipIndex >= placementState.fleet.length) return;

  const cells = grid.previewPlacement(placementState, row, col);
  if (!cells) {
    // Invalid placement, show preview as invalid
    showInvalidPreview(row, col);
    return;
  }

  // Place the ship
  grid.confirmPlacement(placementState, cells);
  rebuildOwnShips();

  grid.clearPreview(defenseTable);
  grid.drawShips(defenseTable, state.ownShips);
  updateShipList();
  updateReadyButton();
}

function showInvalidPreview(row: number, col: number): void {
  if (!defenseTable || !placementState) return;
  const spec = placementState.fleet[placementState.currentShipIndex];
  if (!spec) return;

  const cells: [number, number][] = [];
  for (let i = 0; i < spec.size; i++) {
    const r = placementState.isHorizontal ? row : row + i;
    const c = placementState.isHorizontal ? col + i : col;
    cells.push([r, c]);
  }

  grid.showPlacementPreview(defenseTable, null);
  // Mark cells as invalid
  for (const [r, c] of cells) {
    if (r < state!.gridSize && c < state!.gridSize) {
      const cell = defenseTable.querySelector(`td[data-row="${r}"][data-col="${c}"]`);
      if (cell) cell.classList.add('ss-cell-preview-invalid');
    }
  }

  setTimeout(() => {
    if (defenseTable) grid.clearPreview(defenseTable);
  }, 500);
}

/** Show invalid preview highlight during hover (no timeout — cleared on mouseleave). */
function showInvalidHoverPreview(row: number, col: number): void {
  if (!defenseTable || !placementState) return;
  const spec = placementState.fleet[placementState.currentShipIndex];
  if (!spec) return;

  for (let i = 0; i < spec.size; i++) {
    const r = placementState.isHorizontal ? row : row + i;
    const c = placementState.isHorizontal ? col + i : col;
    if (r < state!.gridSize && c < state!.gridSize) {
      const cell = defenseTable.querySelector(`td[data-row="${r}"][data-col="${c}"]`);
      if (cell) cell.classList.add('ss-cell-preview-invalid');
    }
  }
}

/** Rebuild state.ownShips from current placementState. */
function rebuildOwnShips(): void {
  if (!state || !placementState) return;
  state.ownShips = grid.getPlacedShips(placementState).map(s => ({
    type: s.type,
    size: s.size,
    label: s.label ?? '',
    cells: s.cells,
  }));
}

/** Undo placement of a specific ship by fleet index. */
function handleUndoShip(fleetIndex: number): void {
  if (!state || !placementState || !defenseTable) return;
  const removed = grid.undoPlacement(placementState, fleetIndex);
  if (!removed) return;

  rebuildOwnShips();
  grid.clearPreview(defenseTable);
  grid.drawShips(defenseTable, state.ownShips);
  updateShipList();
  updateReadyButton();
}

function handleRotate(): void {
  if (!placementState) return;
  placementState.isHorizontal = !placementState.isHorizontal;
  if (defenseTable) grid.clearPreview(defenseTable);
  logger.info('[ShipSinking] Rotation:', placementState.isHorizontal ? 'horizontal' : 'vertical');
}

async function handleAutoPlace(): Promise<void> {
  if (!state || !placementState || !defenseTable) return;

  try {
    const res = await api.autoPlace(state.difficulty);

    // Reset placement state and populate all slots
    placementState = grid.createPlacementState(state.gridSize, state.fleet);
    for (let i = 0; i < res.ships.length && i < state.fleet.length; i++) {
      placementState.placedShips[i] = res.ships[i];
    }
    placementState.currentShipIndex = state.fleet.length; // All placed
    rebuildOwnShips();

    grid.drawShips(defenseTable, state.ownShips);
    updateShipList();
    updateReadyButton();
  } catch (err) {
    showError('Auto-place failed. Try again.');
    logger.error('[ShipSinking] autoPlace error:', err);
  }
}

async function handleReady(): Promise<void> {
  if (!state || !placementState) return;
  if (!grid.isAllPlaced(placementState)) return;

  const readyBtn = document.getElementById('readyBtn') as HTMLButtonElement | null;
  if (readyBtn) readyBtn.disabled = true;

  try {
    const ships = state.ownShips.map(s => ({ type: s.type, cells: s.cells }));
    const res = await api.placeFleet(state.gameId, ships);

    if (res.phase === 'combat') {
      state.yourTurn = res.your_turn ?? true;
      enterCombatPhase(undefined, state.abilities);
    }
  } catch (err) {
    showError('Could not submit fleet. Try again.');
    if (readyBtn) readyBtn.disabled = false;
    logger.error('[ShipSinking] placeFleet error:', err);
  }
}

function updateShipList(): void {
  const container = document.getElementById('shipList');
  if (!container || !placementState) return;

  container.innerHTML = '';
  for (let i = 0; i < placementState.fleet.length; i++) {
    const spec = placementState.fleet[i];
    const placed = placementState.placedShips[i] !== null;
    const current = i === placementState.currentShipIndex;

    const div = document.createElement('div');
    div.className = `ss-ship-item ${placed ? 'ss-ship-placed' : ''} ${current ? 'ss-ship-current' : ''}`;

    const label = document.createElement('span');
    label.textContent = `${spec.label} (${spec.size})`;
    div.appendChild(label);

    if (placed) {
      const undoIcon = document.createElement('i');
      undoIcon.className = 'bi bi-x-circle-fill ss-ship-undo ms-2';
      undoIcon.title = 'Remove placement';
      div.appendChild(undoIcon);
      div.addEventListener('click', () => handleUndoShip(i));
    } else if (current) {
      const arrow = document.createElement('i');
      arrow.className = 'bi bi-arrow-left ms-2 text-primary';
      div.appendChild(arrow);
    }

    container.appendChild(div);
  }
}

function updateReadyButton(): void {
  const btn = document.getElementById('readyBtn') as HTMLButtonElement | null;
  if (!btn || !placementState) return;
  btn.disabled = !grid.isAllPlaced(placementState);
}

// ---------------------------------------------------------------------------
// Combat handlers
// ---------------------------------------------------------------------------

async function handleAttackClick(row: number, col: number): Promise<void> {
  if (!state || state.processing || !state.yourTurn) return;

  // If in ability mode, handle ability targeting
  if (state.activeAbility) {
    await handleAbilityTarget(state.activeAbility, row, col);
    return;
  }

  state.processing = true;

  try {
    const res = await api.fireShot(state.gameId, row, col);
    processShotResponse(res);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Shot failed';
    showError(msg);
    logger.error('[ShipSinking] fireShot error:', err);
  } finally {
    state.processing = false;
  }
}

function processShotResponse(res: ShotResponse): void {
  if (!state || !attackTable) return;

  // Animate player's shot on attack grid
  grid.animateShot(attackTable, res.row, res.col, res.result, animationLevel);

  // Reveal sunk ships
  if (res.ships_sunk?.length) {
    for (const ship of res.ships_sunk) {
      grid.revealSunkShip(attackTable, ship, animationLevel);
      showSuccess(`You sunk their ${ship.label}! 💥`);
    }
  }

  state.turnCount = res.turn_count;
  state.yourTurn = res.your_turn;

  // Sync ability unlock status and fleet tracker after each shot
  if (res.abilities) updateAbilities(res.abilities);
  if (res.fleet_tracker) updateFleetTracker(res.fleet_tracker);

  // Process AI counter-action
  if (res.ai_action) {
    processAIAction(res.ai_action, res);
  } else {
    updateTurnIndicator();

    // Check game over (only when no ai_action — ai_action path handles its own game-over)
    if (res.game_over) {
      handleGameOver(res.winner_user_id, res.fp_awarded, res.play_event_id, res.next_game_suggestions as NextGameSuggestions | undefined);
    }
  }
}

function processAIAction(aiAction: AIActionResult, parentRes: ShotResponse | AbilityResultResponse): void {
  if (!state || !defenseTable) return;

  // Delay AI action display for dramatic effect (tier-aware)
  const playfulDelay = 600 + Math.random() * 800;
  const delay = animationLevel === 'none' ? 0
    : animationLevel === 'reduced' ? 300
    : playfulDelay;
  state.yourTurn = false;
  updateTurnIndicator();

  const aiTimerId = window.setTimeout(() => {
    pendingTimers.delete(aiTimerId);
    if (!state || !defenseTable) return;

    if (aiAction.type === 'shot' && aiAction.row != null && aiAction.col != null && aiAction.result) {
      grid.animateShot(defenseTable, aiAction.row, aiAction.col, aiAction.result, animationLevel);
    } else if (aiAction.type === 'ability' && aiAction.results) {
      for (const shot of aiAction.results) {
        grid.animateShot(defenseTable, shot.row, shot.col, shot.result, animationLevel);
      }
    }

    // Reveal sunk own ships
    if (aiAction.ships_sunk?.length && defenseTable) {
      for (const ship of aiAction.ships_sunk) {
        grid.revealSunkShip(defenseTable, ship, animationLevel);
        showInfo(`Your ${ship.label} was sunk!`);
      }
    }

    if (aiAction.game_over) {
      // winner_user_id lives on the parent response, not on ai_action
      handleGameOver(parentRes.winner_user_id, parentRes.fp_awarded, parentRes.play_event_id, parentRes.next_game_suggestions as NextGameSuggestions | undefined);
    } else {
      state!.yourTurn = true;
      updateTurnIndicator();
    }
  }, delay);
  pendingTimers.add(aiTimerId);
}

// ---------------------------------------------------------------------------
// Ability handling
// ---------------------------------------------------------------------------

function toggleAbilityMode(ability: AbilityName): void {
  if (!state || !attackTable) return;

  if (ability === 'depth_charge') {
    // Depth charge uses modal instead of grid targeting
    state.activeAbility = null;
    grid.setAbilityMode(attackTable, null);
    openDepthChargeModal();
    return;
  }

  if (state.activeAbility === ability) {
    // Toggle off
    state.activeAbility = null;
    grid.setAbilityMode(attackTable, null);
    updateAbilityButtons();
    return;
  }

  state.activeAbility = ability;
  grid.setAbilityMode(attackTable, ability);
  updateAbilityButtons();
}

async function handleAbilityTarget(ability: AbilityName, row: number, col: number): Promise<void> {
  if (!state) return;
  state.processing = true;
  state.activeAbility = null;

  if (attackTable) grid.setAbilityMode(attackTable, null);
  updateAbilityButtons();

  try {
    const res = await api.useAbility(state.gameId, ability, { row, col });
    processAbilityResponse(ability, res);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Ability failed';
    showError(msg);
    logger.error('[ShipSinking] useAbility error:', err);
  } finally {
    state.processing = false;
  }
}

function processAbilityResponse(ability: string, res: AbilityResultResponse): void {
  if (!state || !attackTable) return;

  if (ability === 'sonar_ping' && res.scan_area) {
    grid.highlightSonar(attackTable, res.scan_area, res.ship_detected ?? false);
    if (res.ship_detected) {
      showInfo('Sonar detected a ship in that area!');
    } else {
      showInfo('Sonar clear — no ships detected.');
    }
  }

  if (ability === 'spread_bomb' || ability === 'depth_charge') {
    if (res.results) {
      for (const shot of res.results) {
        grid.animateShot(attackTable, shot.row, shot.col, shot.result, animationLevel);
      }
    }
  }

  if (res.ships_sunk?.length) {
    for (const ship of res.ships_sunk) {
      grid.revealSunkShip(attackTable, ship, animationLevel);
      showSuccess(`You sunk their ${ship.label}! 💥`);
    }
  }

  state.turnCount = res.turn_count;
  state.yourTurn = res.your_turn;

  // Mark the ability as used locally
  markAbilityUsed(ability);

  // Sync full ability unlock status and fleet tracker from server
  if (res.abilities) updateAbilities(res.abilities);
  if (res.fleet_tracker) updateFleetTracker(res.fleet_tracker);

  updateAbilityButtons();

  if (res.ai_action) {
    processAIAction(res.ai_action, res);
  } else {
    updateTurnIndicator();

    // Check game over (only when no ai_action — ai_action path handles its own game-over)
    if (res.game_over) {
      handleGameOver(res.winner_user_id, res.fp_awarded, res.play_event_id, res.next_game_suggestions as NextGameSuggestions | undefined);
    }
  }
}

function populateDcIndex(axis: string): void {
  if (!state) return;
  const indexSelect = document.getElementById('dcIndex') as HTMLSelectElement | null;
  if (!indexSelect) return;
  indexSelect.innerHTML = '';
  for (let i = 0; i < state.gridSize; i++) {
    const opt = document.createElement('option');
    opt.value = String(i);
    opt.textContent = axis === 'col' ? String.fromCharCode(65 + i) : String(i + 1);
    indexSelect.appendChild(opt);
  }
}

function openDepthChargeModal(): void {
  if (!state) return;

  const axisSelect = document.getElementById('dcAxis') as HTMLSelectElement | null;
  if (axisSelect) {
    axisSelect.value = 'row';
    axisSelect.onchange = () => populateDcIndex(axisSelect.value);
  }

  populateDcIndex('row');

  const modal = document.getElementById('depthChargeModal');
  if (modal && typeof bootstrap !== 'undefined') {
    const bsModal = new bootstrap.Modal(modal);
    bsModal.show();
  }
}

async function handleDepthChargeFire(): Promise<void> {
  if (!state) return;

  const axisSelect = document.getElementById('dcAxis') as HTMLSelectElement | null;
  const indexSelect = document.getElementById('dcIndex') as HTMLSelectElement | null;
  if (!axisSelect || !indexSelect) return;

  const axis = axisSelect.value;
  const index = parseInt(indexSelect.value, 10);

  // Close modal
  const modal = document.getElementById('depthChargeModal');
  if (modal && typeof bootstrap !== 'undefined') {
    bootstrap.Modal.getInstance(modal)?.hide();
  }

  state.processing = true;

  try {
    const res = await api.useAbility(state.gameId, 'depth_charge', { axis, index });
    processAbilityResponse('depth_charge', res);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Depth charge failed';
    showError(msg);
    logger.error('[ShipSinking] depthCharge error:', err);
  } finally {
    state.processing = false;
  }
}

// ---------------------------------------------------------------------------
// Game over
// ---------------------------------------------------------------------------

function handleGameOver(winnerUserId?: string, fpAwarded?: number, playEventId?: string, nextGameSuggestions?: NextGameSuggestions): void {
  window.__versionGuard?.setSafeToReload(true);
  if (!state) return;
  state.phase = 'finished';
  state.yourTurn = false;
  state.isWin = winnerUserId !== 'ai';
  updateTurnIndicator();

  if (attackTable) grid.disableGrid(attackTable);

  const isWin = winnerUserId !== 'ai';
  const ngs = nextGameSuggestions;

  // Refresh navbar FP balance
  if (fpAwarded && fpAwarded > 0) {
    balanceService.refresh().catch(err => logger.error('FP refresh failed:', err));
  }

  const difficultyLabel = { scout: 'Scout', ensign: 'Ensign', easy: 'Cadet', medium: 'Captain', hard: 'Admiral' }[state.difficulty] ?? state.difficulty;
  const opponentName = state.opponentInfo?.display_name;

  // Build overlay config before orchestrator (no DOM reads inside callback)
  let message: string;
  let subtitle: string;
  if (opponentName) {
    message = isWin
      ? `You defeated ${opponentName}!`
      : `So close! ${opponentName} wins this round.`;
    subtitle = `vs ${opponentName}`;
  } else {
    message = isWin
      ? `Victory! You sunk the enemy fleet in ${state!.turnCount} turns!`
      : 'Your fleet has been sunk!<br>Good battle — try again?';
    subtitle = `${difficultyLabel} difficulty`;
  }

  const onPlayAgain = () => {
    void fireGameCompleted(isGuest);
    resetToSelection();
  };

  const config = isWin
    ? buildSoloWinConfig({
        game: 'ship-sinking',
        totalScore: 0,
        fpEarned: fpAwarded,
        isGuest,
        onPlayAgain,
        playAgainUrl: '/games/ship-sinking',
        playEventId,
        nextGameSuggestions: ngs,
      })
    : buildSoloBustConfig({
        game: 'ship-sinking',
        isGuest,
        onPlayAgain,
        playAgainUrl: '/games/ship-sinking',
        playEventId,
        nextGameSuggestions: ngs,
      });

  // Ship-sinking-specific overrides
  config.message = message;
  config.subtitle = subtitle;
  config.icon = isWin ? 'bi-trophy-fill' : 'bi-crosshair';

  // Cancel any pending EOG sequence
  eogHandle?.cancel();

  // Build a synthetic celebration response (Ship Sinking triggers win celebration directly)
  const celebrationResponse = isWin
    ? { celebrations: [{ preset: 'win', position: 'center' as const }] }
    : {};

  // Orchestrated: celebration → buffer → overlay
  eogHandle = runSequence('ship-sinking-eog', [
    celebrationEffect(celebrationResponse),
    delayEffect(EOG_BUFFER_MS),
  ], () => {
    showEndOfGameOverlay(config);
    hookGuestCompletionToEOG(isGuest);
    loadStats();
  });
}

// ---------------------------------------------------------------------------
// UI updates
// ---------------------------------------------------------------------------

function updateTurnIndicator(): void {
  const el = document.getElementById('turnIndicator');
  if (!el || !state) return;

  if (state.phase === 'finished') {
    const aiLabel = state.opponentInfo?.display_name
      ?? ({ scout: 'Scout', ensign: 'Ensign', easy: 'Cadet', medium: 'Captain', hard: 'Admiral' }[state.difficulty] ?? 'Enemy');
    el.textContent = state.isWin ? 'You won!' : `${aiLabel} won!`;
    el.className = 'ss-turn-indicator text-center mb-3 ss-turn-finished';
    return;
  }

  if (state.yourTurn) {
    el.innerHTML = '<strong>Your Turn</strong> — Fire!';
    el.className = 'ss-turn-indicator text-center mb-3 ss-turn-yours';
  } else {
    const aiLabel = state.opponentInfo?.display_name
      ?? ({ scout: 'Scout', ensign: 'Ensign', easy: 'Cadet', medium: 'Captain', hard: 'Admiral' }[state.difficulty] ?? 'Opponent');
    el.innerHTML = `<em>${aiLabel} is plotting...</em>`;
    el.className = 'ss-turn-indicator text-center mb-3 ss-turn-opponent';
  }
}

interface AbilitiesData {
  [key: string]: { used: boolean; unlocks_at_turn: number; available: boolean };
}

function markAbilityUsed(ability: string): void {
  const btn = document.querySelector<HTMLButtonElement>(`.ss-ability-btn[data-ability="${ability}"]`);
  if (!btn) return;
  btn.disabled = true;
  btn.classList.add('ss-ability-used');
  const tooltip = bootstrap.Tooltip.getInstance(btn);
  if (tooltip) tooltip.setContent({ '.tooltip-inner': 'Already used' });
}

function updateAbilities(abilities: AbilitiesData): void {
  // Store on game state for updateAbilityButtons to reference
  if (state) state.abilities = abilities as AbilitiesMap;

  // Detect locked → available transitions and show weapon unlock card
  for (const [abilityKey, info] of Object.entries(abilities)) {
    const wasAvailable = _prevAbilityAvailable[abilityKey] ?? false;
    const isAvailable = !!info.available && !info.used;
    if (!wasAvailable && isAvailable) {
      showWeaponUnlockCard(abilityKey as AbilityName, (a) => toggleAbilityMode(a));
    }
    _prevAbilityAvailable[abilityKey] = isAvailable;
  }

  const abilityBtns = document.querySelectorAll<HTMLButtonElement>('.ss-ability-btn');
  abilityBtns.forEach(btn => {
    const ability = btn.dataset.ability;
    if (!ability || !abilities[ability]) return;

    const info = abilities[ability];
    const locked = !info.available && !info.used;
    btn.disabled = !info.available || info.used;

    const tooltip = bootstrap.Tooltip.getInstance(btn);
    const desc = btn.dataset.ssDesc || '';

    if (info.used) {
      btn.classList.add('ss-ability-used');
      btn.classList.remove('ss-ability-locked');
      if (tooltip) tooltip.setContent({ '.tooltip-inner': 'Already used' });
    } else if (locked) {
      btn.classList.add('ss-ability-locked');
      btn.classList.remove('ss-ability-used');
      if (tooltip) tooltip.setContent({ '.tooltip-inner': `Unlocks at turn ${info.unlocks_at_turn}` });
    } else {
      btn.classList.remove('ss-ability-used', 'ss-ability-locked');
      if (tooltip) tooltip.setContent({ '.tooltip-inner': desc });
    }

    // Show/hide lock badge
    const badge = btn.querySelector('.ss-ability-lock-badge');
    if (badge) {
      (badge as HTMLElement).style.display = locked ? '' : 'none';
    }
  });
}

function updateAbilityButtons(): void {
  if (!state) return;

  const abilityBtns = document.querySelectorAll<HTMLButtonElement>('.ss-ability-btn');
  abilityBtns.forEach(btn => {
    const ability = btn.dataset.ability as AbilityName | undefined;
    if (!ability) return;

    // Active state styling
    if (state!.activeAbility === ability) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }

    // Disable if not the player's turn, processing, used, or locked
    if (!state!.yourTurn || state!.processing) {
      btn.disabled = true;
    } else if (btn.classList.contains('ss-ability-used') || btn.classList.contains('ss-ability-locked')) {
      btn.disabled = true;
    } else {
      btn.disabled = false;
    }
  });
}

function updateFleetTracker(tracker: GameStateResponse['fleet_tracker']): void {
  const container = document.getElementById('fleetTrackerList');
  if (!container) return;

  container.innerHTML = '';
  for (const ship of tracker) {
    const div = document.createElement('div');
    div.className = `ss-tracker-ship ${ship.sunk ? 'ss-tracker-sunk' : ''}`;

    const dots = '●'.repeat(ship.size);
    div.innerHTML = `
      <span class="ss-tracker-label">${ship.label}</span>
      <span class="ss-tracker-dots">${dots}</span>
      ${ship.sunk ? '<i class="bi bi-x-circle-fill text-danger ms-1"></i>' : ''}
    `;
    container.appendChild(div);
  }
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

async function loadStats(): Promise<void> {
  // Show guest placeholder if not authenticated
  if (isGuest) {
    const statsCard = document.getElementById('statsCard');
    if (statsCard) {
      const cardBody = statsCard.querySelector('.card-body');
      if (cardBody) {
        cardBody.innerHTML = `
          <h6 class="text-muted mb-3">Your Stats</h6>
          <div class="text-center py-3" data-testid="guest-stats-placeholder">
            <p class="mb-1 text-muted">Your game stats will appear here</p>
            <p class="small text-muted mb-2">Available when signed in</p>
            <a href="#" class="btn btn-outline-primary btn-sm" data-testid="guest-stats-signin">
              Sign in to start tracking →
            </a>
          </div>`;
        const signinLink = cardBody.querySelector('[data-testid="guest-stats-signin"]');
        if (signinLink) {
          signinLink.addEventListener('click', (e) => {
            e.preventDefault();
            void fireGuestEvent('tried_locked_feature', true).then((shown) => {
              if (!shown) {
                ensureAuthModalExists();
                authModal.show();
              }
            });
          });
        }
      }
    }
    return;
  }

  try {
    const stats = await api.getStats();
    const winRate = document.getElementById('statWinRate');
    const wins = document.getElementById('statWins');
    const games = document.getElementById('statGames');

    if (winRate) winRate.textContent = stats.total_games > 0 ? `${stats.win_rate}%` : '-';
    if (wins) wins.textContent = stats.total_games > 0 ? String(stats.wins) : '-';
    if (games) games.textContent = stats.total_games > 0 ? String(stats.total_games) : '-';
  } catch {
    logger.warn('[ShipSinking] Could not load stats');
  }
}

// ---------------------------------------------------------------------------
// Reset
// ---------------------------------------------------------------------------

function resetToSelection(): void {
  window.__versionGuard?.setSafeToReload(true);

  // Cancel any pending EOG orchestration
  eogHandle?.cancel();
  eogHandle = undefined;

  // Cancel any in-flight animation timers before clearing state
  for (const id of pendingTimers) {
    window.clearTimeout(id);
  }
  pendingTimers.clear();
  grid.cancelPendingAnimations();

  state = null;
  placementState = null;
  attackTable = null;
  defenseTable = null;

  show('selectionSection');
  hide('placementSection');
  hide('combatSection');

  // Clear grids
  const placementGrid = document.getElementById('placementGrid');
  if (placementGrid) placementGrid.innerHTML = '';
  const attackGrid = document.getElementById('attackGrid');
  if (attackGrid) attackGrid.innerHTML = '';
  const defenseGrid = document.getElementById('defenseGrid');
  if (defenseGrid) defenseGrid.innerHTML = '';

  // Re-mount opponent bar
  const barContainer = document.getElementById('opponentBarContainer');
  if (barContainer) {
    mountOpponentBar({
      container: barContainer,
      gameType: 'ship_sinking',
      initialOpponentId: currentOpponent?.id ?? null,
      onOpponentChange: (opp) => {
        currentOpponent = opp;
      },
    }).then((opponent) => {
      currentOpponent = opponent;
    });
  }

  loadStats();
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', init);
